import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  Landmark,
  Building2,
  Users,
  Briefcase,
  Scale,
  ShieldAlert,
  UserCheck,
  AlertTriangle,
  Mail,
  Rocket,
  CheckCircle2,
  XCircle,
  GitBranch,
  Layers,
  ScrollText,
  Lock,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';
import ProtocolBadge from '../components/ProtocolBadge';
import useRouteMeta from '../hooks/useRouteMeta';

/**
 * Public marketing surface for the European Immigration Accountability Hub.
 *
 * STRICTLY a marketing/education page. NO public analyzer, NO upload form,
 * NO API calls, NO legal-advice claims, NO automatic-decision claims.
 *
 * Doctrine:
 *   AI prepares. Human authority decides. Every action leaves an audit trail.
 *
 * Mounted at:
 *   /dashboard-akt-imigracyjnych   (canonical PL)
 *   /immigration-case-dashboard    (EN alias)
 */

const AUDIENCES = [
  { key: 'legalImmigrants', Icon: UserCheck },
  { key: 'ngos',            Icon: Users },
  { key: 'employers',       Icon: Briefcase },
  { key: 'legalOffices',    Icon: Scale },
  { key: 'municipalities',  Icon: Building2 },
  { key: 'institutions',    Icon: Landmark },
  { key: 'border',          Icon: ShieldAlert },
];

const MODULE_ICONS = [
  GitBranch,    // 1. Identity Cross-Check
  Layers,       // 2. Cross-Border Case Reference
  ScrollText,   // 3. Legal Status Ledger
  AlertTriangle,// 4. Document Conflict Detector
  Rocket,       // 5. Return Procedure Tracker
  UserCheck,    // 6. Legal Immigrant Compliance Portal
  ShieldCheck,  // 7. Human Review Gate
  Lock,         // 8. Audit Trail
];

const CaseHubPublic = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pl').startsWith('en') ? 'en' : 'pl';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = `${origin}/dashboard-akt-imigracyjnych`;

  useRouteMeta({
    marker: 'dashboard-akt-imigracyjnych',
    title: t('caseHubPublic.metaTitle'),
    description: t('caseHubPublic.metaDescription'),
    canonical: pageUrl,
    locale: lang === 'en' ? 'en_GB' : 'pl_PL',
    ogUrl: pageUrl,
  });

  const modules = t('caseHubPublic.modules.items', { returnObjects: true }) || [];
  const notDoing = t('caseHubPublic.notDoing.items', { returnObjects: true }) || [];

  return (
    <div className="hussar-landing" data-testid="case-hub-public-page">
      <HussarTopNav />

      {/* Hero */}
      <section
        className="hussar-section hussar-hero"
        data-testid="casehub-hero"
        style={{ paddingTop: 80 }}
      >
        <div className="hussar-container">
          <div
            data-testid="casehub-hero-eyebrow"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(125, 211, 252, 0.06)',
              color: '#7DD3FC',
              fontSize: 11, letterSpacing: 1.2, fontWeight: 600,
              marginBottom: 18,
            }}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {t('caseHubPublic.heroEyebrow')}
          </div>
          <h1
            className="hussar-hero-title"
            data-testid="casehub-hero-h1"
            style={{ maxWidth: 980 }}
          >
            {t('caseHubPublic.heroH1')}
          </h1>
          <p
            className="hussar-hero-subtitle"
            data-testid="casehub-hero-subtitle"
            style={{ maxWidth: 860, marginTop: 16 }}
          >
            {t('caseHubPublic.heroSubtitle')}
          </p>
          <p
            data-testid="casehub-hero-doctrine"
            style={{
              marginTop: 14,
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(125, 211, 252, 0.08)',
              borderLeft: '3px solid #7DD3FC',
              fontSize: 14,
              fontWeight: 600,
              maxWidth: 860,
            }}
          >
            {t('caseHubPublic.doctrineLine')}
          </p>
          <p
            data-testid="casehub-hero-safety-line"
            style={{
              marginTop: 10,
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(245,158,11,0.06)',
              borderLeft: '3px solid #F59E0B',
              fontSize: 13,
              maxWidth: 860,
            }}
          >
            {t('caseHubPublic.safetyLine')}
          </p>
          <ProtocolBadge testidPrefix="casehub-hero" />
        </div>
      </section>

      {/* Mission */}
      <section
        className="hussar-section"
        data-testid="casehub-mission-section"
        style={{ paddingTop: 16 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="casehub-mission-card"
            style={{
              padding: 24,
              background: 'rgba(125, 211, 252, 0.04)',
              borderLeft: '3px solid #7DD3FC',
            }}
          >
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}
                data-testid="casehub-mission-title">
              {t('caseHubPublic.missionTitle')}
            </h2>
            <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.6, opacity: 0.9 }}
               data-testid="casehub-mission-body">
              {t('caseHubPublic.missionBody')}
            </p>
          </Card>
        </div>
      </section>

      {/* Bridge — person / helper / institution (people-first framing) */}
      <section
        className="hussar-section"
        data-testid="casehub-bridge-section"
        style={{ paddingTop: 28 }}
      >
        <div className="hussar-container">
          <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}
              data-testid="casehub-bridge-heading">
            {t('caseHubPublic.bridge.heading')}
          </h2>
          <p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6, opacity: 0.75 }}
             data-testid="casehub-bridge-subheading">
            {t('caseHubPublic.bridge.subheading')}
          </p>
          <div
            data-testid="casehub-bridge-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16, marginTop: 18,
            }}
          >
            {['people', 'helpers', 'institutions'].map((key, idx) => {
              const Icon = [UserCheck, Users, Landmark][idx];
              return (
                <Card
                  key={key}
                  data-testid={`casehub-bridge-card-${key}`}
                  style={{
                    padding: '20px 18px',
                    background: 'rgba(15,23,42,0.45)',
                    border: '1px solid rgba(125,211,252,0.18)',
                    borderLeft: '3px solid #7DD3FC',
                    borderRadius: 12,
                  }}
                >
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(125, 211, 252, 0.08)', color: '#7DD3FC',
                    }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <h3
                    data-testid={`casehub-bridge-card-${key}-title`}
                    style={{ fontSize: 16, fontWeight: 700, margin: '12px 0 8px', color: '#E2E8F0' }}
                  >
                    {t(`caseHubPublic.bridge.${key}.title`)}
                  </h3>
                  <p
                    data-testid={`casehub-bridge-card-${key}-body`}
                    style={{ fontSize: 13, lineHeight: 1.55, opacity: 0.85, margin: 0 }}
                  >
                    {t(`caseHubPublic.bridge.${key}.body`)}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eight Modules */}
      <section
        className="hussar-section"
        data-testid="casehub-modules-section"
        style={{ paddingTop: 28 }}
      >
        <div className="hussar-container">
          <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}
              data-testid="casehub-modules-heading">
            {t('caseHubPublic.modules.heading')}
          </h2>
          <div
            data-testid="casehub-modules-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16, marginTop: 20,
            }}
          >
            {modules.map((m, idx) => {
              const Icon = MODULE_ICONS[idx] || ShieldCheck;
              return (
                <Card
                  key={m.title}
                  data-testid={`casehub-module-${idx}`}
                  style={{
                    padding: 20,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(125, 211, 252, 0.08)', color: '#7DD3FC',
                    }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, margin: '12px 0 8px' }}>
                    {m.title}
                  </h3>
                  <p style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.85, margin: 0 }}>
                    {m.body}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section
        className="hussar-section"
        data-testid="casehub-audiences-section"
        style={{ paddingTop: 28 }}
      >
        <div className="hussar-container">
          <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}
              data-testid="casehub-audiences-heading">
            {t('caseHubPublic.audiences.heading')}
          </h2>
          <div
            data-testid="casehub-audiences-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 16, marginTop: 20,
            }}
          >
            {AUDIENCES.map(({ key, Icon }) => (
              <Card
                key={key}
                data-testid={`casehub-audience-${key}`}
                style={{
                  padding: 20,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  style={{
                    width: 36, height: 36, borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(125, 211, 252, 0.08)', color: '#7DD3FC',
                  }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 600, margin: '12px 0 8px' }}>
                  {t(`caseHubPublic.audiences.${key}.title`)}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.85, margin: 0 }}>
                  {t(`caseHubPublic.audiences.${key}.body`)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What it does NOT do */}
      <section
        className="hussar-section"
        data-testid="casehub-notdoing-section"
        style={{ paddingTop: 28 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="casehub-notdoing-card"
            style={{ padding: 22, background: 'rgba(239, 68, 68, 0.04)', borderLeft: '3px solid #F87171' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <XCircle className="w-4 h-4" style={{ color: '#F87171' }} />
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}
                  data-testid="casehub-notdoing-heading">
                {t('caseHubPublic.notDoing.heading')}
              </h3>
            </div>
            <ul
              data-testid="casehub-notdoing-list"
              style={{ marginTop: 12, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}
            >
              {notDoing.map((item, idx) => (
                <li key={item} data-testid={`casehub-notdoing-item-${idx}`}>{item}</li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      {/* Cross-border data note (GDPR honesty card) */}
      <section
        className="hussar-section"
        data-testid="casehub-datanote-section"
        style={{ paddingTop: 20 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="casehub-datanote-card"
            style={{
              padding: 22,
              background: 'rgba(255,255,255,0.025)',
              borderLeft: '3px solid #94A3B8',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lock className="w-4 h-4" style={{ color: '#94A3B8' }} />
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}
                  data-testid="casehub-datanote-title">
                {t('caseHubPublic.dataNoteTitle')}
              </h3>
            </div>
            <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, opacity: 0.85 }}
               data-testid="casehub-datanote-body">
              {t('caseHubPublic.dataNoteBody')}
            </p>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section
        className="hussar-section"
        data-testid="casehub-cta-section"
        style={{ paddingTop: 30, paddingBottom: 80 }}
      >
        <div className="hussar-container">
          <Card
            style={{
              padding: 28,
              background: 'rgba(125, 211, 252, 0.05)',
              borderLeft: '3px solid #7DD3FC',
            }}
          >
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}
                data-testid="casehub-cta-headline">
              {t('caseHubPublic.cta.headline')}
            </h2>
            <p style={{ marginTop: 10, fontSize: 15, opacity: 0.9, lineHeight: 1.55 }}
               data-testid="casehub-cta-body">
              {t('caseHubPublic.cta.body')}
            </p>
            <div style={{ marginTop: 18, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link
                to={t('caseHubPublic.cta.primaryTo')}
                className="hussar-btn-primary"
                data-testid="casehub-cta-primary"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px', borderRadius: 8,
                  textDecoration: 'none', fontWeight: 600,
                }}
              >
                {t('caseHubPublic.cta.primary')} <Rocket className="w-4 h-4" />
              </Link>
              <Link
                to="/moje-akta-imigracyjne"
                data-testid="casehub-cta-start-record"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px', borderRadius: 8,
                  border: '1px solid rgba(125,211,252,0.36)',
                  background: 'rgba(125,211,252,0.06)',
                  color: '#7DD3FC',
                  textDecoration: 'none', fontWeight: 600,
                }}
              >
                {t('mojeAkta.ctaLabel')} <UserCheck className="w-4 h-4" />
              </Link>
              <a
                href="mailto:audyt@hussarai.org?subject=Hub%20Akt%20Imigracyjnych"
                className="hussar-btn-secondary"
                data-testid="casehub-cta-secondary"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.12)',
                  textDecoration: 'none', fontWeight: 600,
                  color: 'var(--hussar-text-primary, #E5E7EB)',
                }}
              >
                {t('caseHubPublic.cta.secondary')} <Mail className="w-4 h-4" />
              </a>
            </div>
          </Card>

          <p
            data-testid="casehub-footer-doctrine"
            style={{
              marginTop: 22, fontSize: 12, opacity: 0.6,
              textAlign: 'left', fontStyle: 'italic',
            }}
          >
            <AlertTriangle className="w-3 h-3 inline mr-1" style={{ verticalAlign: '-2px' }} />
            {t('caseHubPublic.doctrineLine')}
          </p>
          <p
            data-testid="casehub-footer-meta"
            style={{
              marginTop: 6, fontSize: 12, opacity: 0.45,
              textAlign: 'left',
            }}
          >
            {t('caseHubPublic.footerMeta')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default CaseHubPublic;
